/**
 * Auth Guard
 * Protects routes and handles authentication checks
 */

import type { RouteLocationNormalized, NavigationGuardReturn } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

/**
 * Public routes that don't require authentication
 */
const publicRoutes = ['/', '/login', '/register', '/forgot-password', '/magic-link', '/auth/action', '/auth/magic-link']

/**
 * Routes that should redirect to dashboard if already authenticated
 */
const authOnlyRoutes = ['/login', '/register']

/**
 * Auth guard middleware
 * Checks authentication status and redirects as needed
 *
 * IMPORTANT: Waits for Firebase to initialize on page refresh to prevent
 * the "not logged in" flash when Firebase is restoring the session
 *
 * @param to - Route navigating to
 * @param from - Route navigating from
 * @returns Navigation guard result
 */
export async function authGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized
): Promise<NavigationGuardReturn> {
  const authStore = useAuthStore()
  const { getCurrentUser } = await import('@/services/auth')

  // Wait for Firebase to initialize (only needed on page refresh)
  // This prevents the race condition where:
  // 1. Page loads, token in localStorage
  // 2. onAuthStateChanged fires with null (not initialized yet)
  // 3. Store gets cleared
  // 4. Firebase restores session
  // 5. User sees flash of "not logged in"
  if (!authStore.isInitialized) {
    await new Promise<void>((resolve) => {
      const maxWaitTime = 2000 // 2 seconds max
      const checkInterval = 50 // Check every 50ms
      let elapsed = 0

      const interval = setInterval(() => {
        elapsed += checkInterval

        // Check if Firebase has a current user (session restored)
        const fbUser = getCurrentUser()

        if (authStore.isInitialized || fbUser !== null || elapsed >= maxWaitTime) {
          clearInterval(interval)
          resolve()
        }
      }, checkInterval)
    })
  }

  // Now check authentication with confidence that Firebase has initialized
  const isAuthenticated = authStore.isAuthenticated || getCurrentUser() !== null

  const isPublicRoute = publicRoutes.includes(to.path)

  // If navigating to root path and already authenticated, redirect to dashboard
  if (to.path === '/' && isAuthenticated) {
    return { name: 'dashboard' }
  }

  // If navigating to auth-only route (login/register) and already authenticated
  if (authOnlyRoutes.includes(to.path) && isAuthenticated) {
    return { name: 'dashboard' }
  }

  // If navigating to protected route (not public) and not authenticated
  if (!isPublicRoute && !isAuthenticated) {
    return {
      name: 'home',
      query: { redirect: to.path },
    }
  }

  // Allow navigation
  return true
}
